/**
 * /sub-pay-apps/:payAppId — Pay Application Detail
 *
 * Tabs: Summary | G703 Lines | Documents | History
 */
import { createFileRoute } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptIcon from "@mui/icons-material/Receipt";

import { api } from "@/lib/api";
import { subPayAppDetailQueryOptions, subPayAppKeys } from "@/lib/queries";
import { formatMoney } from "@/lib/money";
import type { SubPayApplication, SubPayAppStatus } from "@/lib/types";
import { SubPayAppGrid } from "@/components/SubPayAppGrid";
import { SubPayAppDocumentsList } from "@/components/SubPayAppDocumentsList";

export const Route = createFileRoute("/_auth/sub-pay-apps/$payAppId")({
  component: SubPayAppDetailPage,
});

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_COLOR: Record<
  SubPayAppStatus,
  "default" | "primary" | "warning" | "success" | "error" | "secondary" | "info"
> = {
  draft: "default",
  submitted: "primary",
  under_review: "warning",
  approved: "success",
  rejected: "error",
  invoiced: "secondary",
  paid: "info",
  void: "default",
};

const STATUS_LABEL: Record<SubPayAppStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under Review",
  approved: "Approved",
  rejected: "Rejected",
  invoiced: "Invoiced",
  paid: "Paid",
  void: "Void",
};

function fmtMoney(v: number) {
  return formatMoney(v) ?? "—";
}

function fmtPct(v: number) {
  return `${(v * 100).toFixed(1)}%`;
}

// ─── Rejection dialog ─────────────────────────────────────────────────────────

function RejectDialog({
  open,
  payAppId,
  onClose,
}: {
  open: boolean;
  payAppId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/sub-pay-apps/${payAppId}/reject`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subPayAppKeys.detail(payAppId) });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject Pay Application</DialogTitle>
      <DialogContent sx={{ mt: 1 }}>
        <TextField
          label="Reason"
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={!reason || mutation.isPending}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Approve dialog ───────────────────────────────────────────────────────────

function ApproveDialog({
  open,
  payAppId,
  onClose,
}: {
  open: boolean;
  payAppId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [retentionOverride, setRetentionOverride] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/sub-pay-apps/${payAppId}/approve`, {
        method: "POST",
        body: {
          retention_override_pct: retentionOverride
            ? parseFloat(retentionOverride) / 100
            : null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subPayAppKeys.detail(payAppId) });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Approve Pay Application</DialogTitle>
      <DialogContent sx={{ mt: 1 }}>
        <TextField
          label="Retention Override % (optional)"
          type="number"
          value={retentionOverride}
          onChange={(e) => setRetentionOverride(e.target.value)}
          helperText="Leave blank to use existing retention %"
          fullWidth
        />
        {mutation.isError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {String((mutation.error as Error)?.message ?? "Error")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="success"
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Header w/ action buttons ─────────────────────────────────────────────────

function PayAppHeader({ payApp }: { payApp: SubPayApplication }) {
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  const submitMutation = useMutation({
    mutationFn: () =>
      api(`/api/sub-pay-apps/${payApp.id}/submit`, { method: "POST" }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: subPayAppKeys.detail(payApp.id) }),
  });

  const postInvoiceMutation = useMutation({
    mutationFn: () =>
      api(`/api/sub-pay-apps/${payApp.id}/post-invoice`, { method: "POST" }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: subPayAppKeys.detail(payApp.id) }),
  });

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Chip
        label={STATUS_LABEL[payApp.status]}
        color={STATUS_COLOR[payApp.status]}
      />

      {payApp.status === "draft" && (
        <Button
          variant="contained"
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
          startIcon={<ReceiptIcon />}
        >
          Submit for Review
        </Button>
      )}

      {payApp.status === "submitted" && (
        <>
          <Button
            variant="contained"
            color="success"
            onClick={() => setApproveOpen(true)}
            startIcon={<CheckCircleIcon />}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setRejectOpen(true)}
            startIcon={<CancelIcon />}
          >
            Reject
          </Button>
        </>
      )}

      {payApp.status === "approved" && (
        <Button
          variant="contained"
          onClick={() => postInvoiceMutation.mutate()}
          disabled={postInvoiceMutation.isPending}
        >
          Post Invoice
        </Button>
      )}

      {payApp.status === "invoiced" && (
        <Chip label="Awaiting Payment" color="secondary" />
      )}

      {payApp.status === "paid" && (
        <Chip label="Paid" color="info" />
      )}

      <Button
        href={`/api/sub-pay-apps/${payApp.id}/pdf`}
        target="_blank"
        size="small"
        variant="outlined"
      >
        Download PDF
      </Button>

      <RejectDialog
        open={rejectOpen}
        payAppId={payApp.id}
        onClose={() => setRejectOpen(false)}
      />
      <ApproveDialog
        open={approveOpen}
        payAppId={payApp.id}
        onClose={() => setApproveOpen(false)}
      />
    </Box>
  );
}

// ─── Summary tab ─────────────────────────────────────────────────────────────

function SummaryTab({ payApp }: { payApp: SubPayApplication }) {
  const fields: [string, string][] = [
    ["Application No.", String(payApp.application_no)],
    ["Period", `${payApp.period_start} – ${payApp.period_end}`],
    ["Retention %", fmtPct(payApp.retention_pct)],
    ["Total Completed & Stored", fmtMoney(payApp.total_completed_and_stored)],
    ["Retention Held", fmtMoney(payApp.retention_held_to_date)],
    ["Total Earned Less Retention", fmtMoney(payApp.total_earned_less_retention)],
    ["Prior Payments", fmtMoney(payApp.prior_payments_total)],
    ["Current Payment Due", fmtMoney(payApp.current_payment_due)],
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 2,
        }}
      >
        {fields.map(([label, value]) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      {payApp.notes && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Notes
          </Typography>
          <Typography variant="body2">{payApp.notes}</Typography>
        </>
      )}
      {payApp.rejected_reason && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="error">
            Rejection Reason
          </Typography>
          <Typography variant="body2" color="error">
            {payApp.rejected_reason}
          </Typography>
        </>
      )}
    </Paper>
  );
}

// ─── History tab ─────────────────────────────────────────────────────────────

function HistoryTab({ payApp }: { payApp: SubPayApplication }) {
  const events: { label: string; at: string | null }[] = [
    { label: "Created", at: payApp.created_at },
    { label: "Submitted", at: payApp.submitted_date },
    { label: "Approved", at: payApp.approved_at },
  ].filter((e) => e.at != null);

  return (
    <Box sx={{ mt: 2 }}>
      {events.map((e) => (
        <Box key={e.label} sx={{ display: "flex", gap: 2, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: 120 }}>
            {new Date(e.at!).toLocaleString()}
          </Typography>
          <Typography variant="body2">{e.label}</Typography>
        </Box>
      ))}
      {events.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No history available
        </Typography>
      )}
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function SubPayAppDetailPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const { payAppId } = Route.useParams();
  const [tab, setTab] = useState(0);

  const { data: payApp, isLoading } = useQuery(subPayAppDetailQueryOptions(payAppId));

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!payApp) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Pay application not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Pay Application #{payApp.application_no}
      </Typography>
      <PayAppHeader payApp={payApp} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
        <Tab label="Summary" />
        <Tab label="G703 Lines" />
        <Tab label="Documents" />
        <Tab label="History" />
      </Tabs>

      {tab === 0 && <SummaryTab payApp={payApp} />}
      {tab === 1 && <SubPayAppGrid payApp={payApp} />}
      {tab === 2 && <SubPayAppDocumentsList payApp={payApp} />}
      {tab === 3 && <HistoryTab payApp={payApp} />}
    </Box>
  );
}
